import React, { useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { X, Send, Copy, ShieldCheck, Mail } from "lucide-react";
import { useT } from "../lib/i18n";

export default function ShareDialog({ sessionId, sessionTitle, open, onClose }) {
  const { t } = useT();
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [note, setNote] = useState("");
  const [expiresDays, setExpiresDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const submit = async () => {
    if (!recipientEmail.includes("@")) {
      toast.error(t("Please enter a valid email"));
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/sessions/${sessionId}/share`, {
        recipient_email: recipientEmail,
        recipient_name: recipientName || null,
        note: note || null,
        frontend_origin: window.location.origin,
        expires_days: Number(expiresDays),
      });
      setResult(r.data);
      if (r.data.email_status === "sent") toast.success(`Email sent to ${recipientEmail}`);
      else if (r.data.email_status === "skipped") toast.warning("Link generated. Email not sent (Resend key not configured).");
      else toast.error(`Email failed: ${r.data.email_error || "unknown"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Could not share"));
    } finally { setSubmitting(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(result.link); toast.success(t("Link copied")); } catch (e) { toast.error("Copy failed"); }
  };

  const reset = () => {
    setResult(null); setRecipientEmail(""); setRecipientName(""); setNote("");
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-[#0B1B3D]/40 backdrop-blur-sm" data-testid="share-dialog">
      <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#0EA5E9]" />
            <p className="font-display font-extrabold text-[#0B1B3D]">{t("Send to your advisor")}</p>
          </div>
          <button data-testid="share-close-btn" onClick={reset} className="text-[#94A3B8] hover:text-[#0B1B3D]"><X className="w-5 h-5" /></button>
        </div>

        {!result && (
          <div className="p-6 space-y-4">
            <p className="text-sm text-[#475569]">Share <span className="font-bold text-[#0B1B3D]">{sessionTitle}</span> with your accountant, lawyer, CFO or any external expert. They'll get a read-only review link.</p>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-[#0B1B3D] font-display">{t("Recipient email")}</label>
                <input data-testid="share-email-input" type="email" required value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="advisor@firm.com" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-[#0EA5E9] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#0B1B3D] font-display">{t("Name (optional)")}</label>
                <input data-testid="share-name-input" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Anna Müller" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-[#0EA5E9] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20" />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-[#0B1B3D] font-display">{t("Note (optional)")}</label>
              <textarea data-testid="share-note-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Hi Anna, could you review the tax angle on this report?" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-[#0EA5E9] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20" />
            </div>

            <div>
              <label className="text-xs font-bold text-[#0B1B3D] font-display">{t("Link expires in")}</label>
              <select data-testid="share-expiry-select" value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-[#0EA5E9] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20 bg-white">
                <option value={7}>7 {t("days")}</option>
                <option value={14}>14 {t("days")}</option>
                <option value={30}>30 {t("days")}</option>
              </select>
            </div>

            <div className="flex items-start gap-2 text-xs text-[#475569] bg-[#F8FAFC] border border-slate-200 rounded-lg p-3">
              <ShieldCheck className="w-4 h-4 text-[#10B981] mt-0.5" />
              <p>{t("The recipient gets a unique, read-only link. They cannot edit your report or trigger new sessions.")}</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button data-testid="share-cancel-btn" onClick={reset} className="text-sm font-bold text-[#475569] px-4 py-2.5 hover:text-[#0B1B3D]">{t("Cancel")}</button>
              <button data-testid="share-submit-btn" disabled={submitting} onClick={submit} className="bg-[#0B1B3D] disabled:opacity-50 text-white hover:bg-[#1E3A8A] px-5 py-2.5 rounded-lg font-bold text-sm font-display inline-flex items-center gap-2"><Send className="w-4 h-4" /> {submitting ? t("Sending…") : t("Send report")}</button>
            </div>
          </div>
        )}

        {result && (
          <div className="p-6 space-y-4" data-testid="share-result">
            <p className="font-display font-extrabold text-[#0B1B3D] text-lg">{t("Review link generated")}</p>
            <p className="text-sm text-[#475569]">
              {result.email_status === "sent" && <>Email delivered to <span className="font-bold text-[#0B1B3D]">{recipientEmail}</span>.</>}
              {result.email_status === "skipped" && <>Email not sent — Resend key not configured yet. Share the link manually:</>}
              {result.email_status === "failed" && <>Email failed: <span className="text-[#EF4444]">{result.email_error}</span>. You can still share the link manually.</>}
            </p>
            <div className="flex items-center gap-2 bg-[#F8FAFC] border border-slate-200 rounded-lg p-3">
              <code data-testid="share-result-link" className="font-mono-kz text-xs text-[#0B1B3D] flex-1 truncate">{result.link}</code>
              <button data-testid="share-copy-btn" onClick={copyLink} className="text-[#0EA5E9] hover:text-[#0B1B3D]"><Copy className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-[#94A3B8] font-mono-kz">Expires {new Date(result.expires_at).toLocaleDateString()}</p>
            <div className="flex justify-end pt-2">
              <button data-testid="share-done-btn" onClick={reset} className="bg-[#0B1B3D] text-white hover:bg-[#1E3A8A] px-5 py-2.5 rounded-lg font-bold text-sm font-display">{t("Done")}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
